import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { ClientNavbar } from '../components/ClientNavbar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Upload, AlertTriangle, History, ArrowDown, ArrowUp } from 'lucide-react';

import { API } from '../utils/api';

export default function Profile() {
    const { user, setUser } = useAuth();
    
    return (
        <div className="min-h-screen bg-background md:ml-64" data-testid="profile-page">
            <ClientNavbar />
            
            <main className="max-w-3xl mx-auto px-4 py-8 md:py-12">
                <div className="mb-10">
                    <div className="flex items-center gap-2 mb-2">
                        <div className="h-1 w-8 bg-[#4c56af] rounded-full" />
                        <span className="text-[10px] uppercase tracking-widest font-black text-[#4c56af]">
                            MI CUENTA
                        </span>
                    </div>
                    <h2 className="text-4xl font-black text-[#001944] dark:text-white tracking-tight uppercase">
                        Ajustes de Perfil
                    </h2>
                </div>

                <div className="space-y-8">
                    {/* Tarjeta de Usuario/Avatar */}
                    <Card className="border-0 bg-slate-50 dark:bg-[#001944] rounded-3xl shadow-sm overflow-hidden">
                        <div className="h-24 bg-gradient-to-r from-[#001944] to-[#4c56af]" />
                        <CardContent className="relative px-6 pb-8">
                            <div className="flex flex-col sm:flex-row sm:items-end gap-6 -mt-12 sm:-mt-10">
                                <div className="relative">
                                    <Avatar className="w-24 h-24 sm:w-28 sm:h-28 border-4 border-slate-50 dark:border-[#001944] shadow-xl bg-white dark:bg-[#122550]">
                                        {user?.picture?.startsWith('/') ? (
                                            <AvatarImage src={`${API.replace('/backup/api', '').replace('/api', '')}${user.picture}`} alt={user?.name} />
                                        ) : (
                                            <AvatarImage src={user?.picture} alt={user?.name} />
                                        )}
                                        <AvatarFallback className="text-3xl bg-slate-200 dark:bg-[#122550] text-[#001944] dark:text-white font-black">
                                            {user?.name?.charAt(0)}
                                        </AvatarFallback>
                                    </Avatar>
                                </div>
                                <div className="pb-1">
                                    <h1 className="text-2xl font-black text-[#001944] dark:text-white tracking-tight leading-none mb-1">
                                        {user?.name}
                                    </h1>
                                    <p className="text-sm font-medium text-slate-500 dark:text-slate-400">
                                        {user?.email}
                                    </p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <ProfileSettings user={user} setUser={setUser} />
                </div>
            </main>
        </div>
    );
}

function ProfileSettings({ user, setUser }) {
    const [loadingProfile, setLoadingProfile] = useState(false);
    const [loadingPassword, setLoadingPassword] = useState(false);
    const [loadingGoogle, setLoadingGoogle] = useState(false);
    const [uploadingAvatar, setUploadingAvatar] = useState(false);
    const fileInputRef = useRef(null);
    
    const [formData, setFormData] = useState({
        name: user?.name || '',
        phone: user?.phone || '',
        cedula: user?.cedula || '',
        city: user?.city || ''
    });

    const [transactions, setTransactions] = useState([]);
    const [loadingTx, setLoadingTx] = useState(false);

    useEffect(() => {
        const fetchTransactions = async () => {
            try {
                setLoadingTx(true);
                const res = await axios.get(`${API}/users/profile/transactions`, { withCredentials: true });
                setTransactions(res.data.transactions || []);
            } catch (err) {
                console.error("Error fetching transactions", err);
            } finally {
                setLoadingTx(false);
            }
        };
        fetchTransactions();
    }, []);

    useEffect(() => {
        if (user) {
            setFormData({
                name: user.name || '',
                phone: user.phone || '',
                cedula: user.cedula || '',
                city: user.city || ''
            });
        }
    }, [user]);

    const [passwordData, setPasswordData] = useState({
        current_password: '',
        new_password: '',
        confirm_password: ''
    });

    const [googleDisconnect, setGoogleDisconnect] = useState({
        new_password: '',
        confirm_password: ''
    });

    const handleProfileSubmit = async (e) => {
        e.preventDefault();
        setLoadingProfile(true);
        try {
            await axios.put(`${API}/users/profile`, formData, { withCredentials: true });
            if (setUser) setUser({ ...user, ...formData });
            toast.success('Perfil actualizado correctamente');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error al actualizar perfil');
        } finally {
            setLoadingProfile(false);
        }
    };

    const handlePasswordSubmit = async (e) => {
        e.preventDefault();
        if (passwordData.new_password !== passwordData.confirm_password) {
            toast.error('Las contraseñas nuevas no coinciden');
            return;
        }
        setLoadingPassword(true);
        try {
            await axios.put(`${API}/users/password`, {
                current_password: passwordData.current_password,
                new_password: passwordData.new_password
            }, { withCredentials: true });
            setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
            toast.success('Contraseña actualizada correctamente');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error al cambiar contraseña');
        } finally {
            setLoadingPassword(false);
        }
    };

    const handleGoogleDisconnect = async (e) => {
        e.preventDefault();
        if (googleDisconnect.new_password !== googleDisconnect.confirm_password) {
            toast.error('Las contraseñas no coinciden');
            return;
        }
        setLoadingGoogle(true);
        try {
            await axios.put(`${API}/users/disconnect-google`, {
                new_password: googleDisconnect.new_password
            }, { withCredentials: true });
            if (setUser) setUser({ ...user, auth_provider: 'email' });
            setGoogleDisconnect({ new_password: '', confirm_password: '' });
            toast.success('Cuenta desconectada exitosamente. Ahora usarás correo y contraseña.');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error al desconectar Google');
        } finally {
            setLoadingGoogle(false);
        }
    };

    const handleAvatarUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            toast.error('Por favor selecciona una imagen');
            return;
        }

        if (file.size > 5 * 1024 * 1024) { // 5MB Limit
            toast.error('La imagen no debe superar los 5MB');
            return;
        }

        const formData = new FormData();
        formData.append('file', file);

        setUploadingAvatar(true);
        try {
            const res = await axios.post(`${API}/users/avatar`, formData, {
                withCredentials: true,
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            if (setUser) setUser({ ...user, picture: res.data.picture });
            toast.success('Foto de perfil actualizada');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error al subir la imagen');
        } finally {
            setUploadingAvatar(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    return (
        <div className="space-y-6">
            {/* Datos Personales */}
            <Card className="border-0 bg-slate-50 dark:bg-[#001944] rounded-3xl shadow-sm">
                <CardHeader className="pb-2 px-6 pt-6">
                    <CardTitle className="text-lg font-black text-[#001944] dark:text-white uppercase tracking-tight">Datos Personales</CardTitle>
                </CardHeader>
                <CardContent className="px-6 pb-6">
                    <form onSubmit={handleProfileSubmit} className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-2">
                                <Label htmlFor="name" className="text-xs font-black uppercase text-slate-500">Nombre</Label>
                                <Input id="name" className="h-12 rounded-xl bg-white dark:bg-[#122550] border-slate-200 dark:border-white/5" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} required />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="phone" className="text-xs font-black uppercase text-slate-500">Celular</Label>
                                <Input id="phone" className="h-12 rounded-xl bg-white dark:bg-[#122550] border-slate-200 dark:border-white/5" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="cedula" className="text-xs font-black uppercase text-slate-500">Cédula</Label>
                                <Input id="cedula" className="h-12 rounded-xl bg-white dark:bg-[#122550] border-slate-200 dark:border-white/5" value={formData.cedula} onChange={e => setFormData({...formData, cedula: e.target.value})} />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="city" className="text-xs font-black uppercase text-slate-500">Ciudad</Label>
                                <Input id="city" className="h-12 rounded-xl bg-white dark:bg-[#122550] border-slate-200 dark:border-white/5" value={formData.city} onChange={e => setFormData({...formData, city: e.target.value})} />
                            </div>
                        </div>
                        <Button type="submit" disabled={loadingProfile} className="h-12 px-8 rounded-xl font-bold bg-[#4c56af] hover:bg-[#3d458c] text-white">
                            {loadingProfile ? 'Guardando...' : 'Guardar Cambios'}
                        </Button>
                    </form>
                </CardContent>
            </Card>

            {/* Avatar (solo para auth email) */}
            {user?.auth_provider !== 'google' && (
                <Card className="border-0 bg-slate-50 dark:bg-[#001944] rounded-3xl shadow-sm">
                    <CardHeader className="pb-2 px-6 pt-6">
                        <CardTitle className="text-lg font-black text-[#001944] dark:text-white uppercase tracking-tight">Foto de Perfil</CardTitle>
                    </CardHeader>
                    <CardContent className="px-6 pb-6">
                        <div className="flex items-center gap-4">
                            <input 
                                type="file" 
                                accept="image/*" 
                                className="hidden" 
                                ref={fileInputRef} 
                                onChange={handleAvatarUpload} 
                            />
                            <Button 
                                type="button" 
                                variant="outline" 
                                disabled={uploadingAvatar}
                                onClick={() => fileInputRef.current?.click()}
                                className="h-12 px-6 rounded-xl font-bold border-slate-200 dark:border-white/10"
                            >
                                <Upload className="w-4 h-4 mr-2" />
                                {uploadingAvatar ? 'Subiendo...' : 'Cambiar Foto'}
                            </Button>
                            <span className="text-xs text-slate-500">JPG, PNG (Máx 5MB)</span>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Seguridad */}
            {user?.auth_provider !== 'google' ? (
                <Card className="border-0 bg-slate-50 dark:bg-[#001944] rounded-3xl shadow-sm">
                    <CardHeader className="pb-2 px-6 pt-6">
                        <CardTitle className="text-lg font-black text-[#001944] dark:text-white uppercase tracking-tight">Contraseña</CardTitle>
                    </CardHeader>
                    <CardContent className="px-6 pb-6">
                        <form onSubmit={handlePasswordSubmit} className="space-y-6 max-w-sm">
                            <div className="space-y-2">
                                <Label htmlFor="current_password" className="text-xs font-black uppercase text-slate-500">Contraseña Actual</Label>
                                <Input id="current_password" type="password" className="h-12 rounded-xl bg-white dark:bg-[#122550] border-slate-200 dark:border-white/5" value={passwordData.current_password} onChange={e => setPasswordData({...passwordData, current_password: e.target.value})} required />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="new_password" className="text-xs font-black uppercase text-slate-500">Nueva Contraseña</Label>
                                <Input id="new_password" type="password" className="h-12 rounded-xl bg-white dark:bg-[#122550] border-slate-200 dark:border-white/5" value={passwordData.new_password} onChange={e => setPasswordData({...passwordData, new_password: e.target.value})} required minLength={6} />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="confirm_password" className="text-xs font-black uppercase text-slate-500">Confirmar Nueva Contraseña</Label>
                                <Input id="confirm_password" type="password" className="h-12 rounded-xl bg-white dark:bg-[#122550] border-slate-200 dark:border-white/5" value={passwordData.confirm_password} onChange={e => setPasswordData({...passwordData, confirm_password: e.target.value})} required minLength={6} />
                            </div>
                            <Button type="submit" variant="destructive" disabled={loadingPassword} className="h-12 rounded-xl font-bold bg-slate-900 dark:bg-red-500/20 hover:bg-slate-800 dark:hover:bg-red-500/30 text-white dark:text-red-500">
                                {loadingPassword ? 'Actualizando...' : 'Actualizar Contraseña'}
                            </Button>
                        </form>
                    </CardContent>
                </Card>
            ) : (
                <Card className="border border-red-500/20 bg-red-500/5 rounded-3xl shadow-sm">
                    <CardHeader className="pb-2 px-6 pt-6 flex flex-row items-center gap-2">
                        <AlertTriangle className="w-5 h-5 text-red-500" />
                        <CardTitle className="text-lg font-black text-red-500 uppercase tracking-tight m-0">Desconectar Google</CardTitle>
                    </CardHeader>
                    <CardContent className="px-6 pb-6">
                        <p className="text-sm text-slate-600 dark:text-slate-400 mb-6 max-w-lg">
                            Actualmente inicias sesión con Google. Si desconectas tu cuenta de Google, se convertirá en una cuenta estándar y necesitarás una contraseña para iniciar sesión en el futuro.
                        </p>
                        <form onSubmit={handleGoogleDisconnect} className="space-y-6 max-w-sm">
                            <div className="space-y-2">
                                <Label htmlFor="g_new_password" className="text-xs font-black uppercase text-slate-500">Crear Contraseña</Label>
                                <Input id="g_new_password" type="password" className="h-12 rounded-xl bg-white dark:bg-[#122550] border-slate-200 dark:border-white/5" value={googleDisconnect.new_password} onChange={e => setGoogleDisconnect({...googleDisconnect, new_password: e.target.value})} required minLength={6} placeholder="Mínimo 6 caracteres" />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="g_confirm_password" className="text-xs font-black uppercase text-slate-500">Confirmar Contraseña</Label>
                                <Input id="g_confirm_password" type="password" className="h-12 rounded-xl bg-white dark:bg-[#122550] border-slate-200 dark:border-white/5" value={googleDisconnect.confirm_password} onChange={e => setGoogleDisconnect({...googleDisconnect, confirm_password: e.target.value})} required minLength={6} />
                            </div>
                            <Button type="submit" variant="destructive" disabled={loadingGoogle} className="h-12 rounded-xl font-bold">
                                {loadingGoogle ? 'Procesando...' : 'Desconectar y Guardar'}
                            </Button>
                        </form>
                    </CardContent>
                </Card>
            )}

            {/* Historial de Créditos */}
            <Card className="border-0 bg-slate-50 dark:bg-[#001944] rounded-3xl shadow-sm overflow-hidden">
                <CardHeader className="pb-2 px-6 pt-6 flex flex-row items-center gap-2">
                    <History className="w-5 h-5 text-[#4c56af] dark:text-white" />
                    <CardTitle className="text-lg font-black text-[#001944] dark:text-white uppercase tracking-tight m-0">Historial de Créditos</CardTitle>
                </CardHeader>
                <CardContent className="px-6 pb-6 mt-4">
                    {loadingTx ? (
                        <div className="flex justify-center p-4"><span className="text-slate-500">Cargando...</span></div>
                    ) : transactions.length === 0 ? (
                        <div className="text-center p-8 text-slate-500 rounded-xl bg-white dark:bg-[#122550] border border-slate-100 dark:border-white/5">
                            No hay transacciones registradas.
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {transactions.map((tx) => {
                                const isPositive = tx.amount > 0;
                                return (
                                    <div key={tx._id} className="flex items-center justify-between p-4 rounded-xl bg-white dark:bg-[#122550] border border-slate-100 dark:border-white/5">
                                        <div className="flex items-center gap-4">
                                            <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${isPositive ? 'bg-green-100 text-green-600 dark:bg-green-500/20' : 'bg-red-100 text-red-600 dark:bg-red-500/20'}`}>
                                                {isPositive ? <ArrowUp className="w-5 h-5" /> : <ArrowDown className="w-5 h-5" />}
                                            </div>
                                            <div>
                                                <p className="text-sm font-bold text-[#001944] dark:text-white">{tx.description || (tx.transaction_type === 'shopify' ? 'Compra Shopify' : 'Ajuste Manual')}</p>
                                                <p className="text-xs text-slate-500">{new Date(tx.created_at).toLocaleString()}</p>
                                            </div>
                                        </div>
                                        <div className={`text-base font-black ${isPositive ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                            {isPositive ? '+' : ''}{tx.amount}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
